import { Head, Link } from "@inertiajs/react";
import { Plus, BookOpen, Calendar, ArrowRight } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import StatusBadge from "@/components/shared/StatusBadge";
import EmptyState from "@/components/EmptyState";
import { formatDate, formatDateRange, formatMoney, formatNumber, pluralize } from "@/lib/format";
import { Badge } from "@/components/ui/badge";

export default function Index({ school, membership, campaigns, new_campaign_path }) {
  // Only one campaign runs at a time, so a new one isn't the main action
  // while one is live.
  const running = campaigns.some((c) => c.state === "active" || c.state === "grace");

  return (
    <SchoolLayout
      school={school}
      membership={membership}
      title="Campaigns"
      actions={
        membership.school_admin && campaigns.length > 0 && (
          <Button variant={running ? "outline" : "default"} asChild>
            <Link href={new_campaign_path}>
              <Plus className="h-4 w-4" />
              New campaign
            </Link>
          </Button>
        )
      }
    >
      <Head title={`${school.name} · Campaigns`} />

      {campaigns.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="No campaigns yet"
          description="A campaign is one read-a-thon: its dates, goals and everyone taking part. The setup guide walks you through your first."
          action={membership.school_admin && (
            <Button asChild>
              <Link href={new_campaign_path}>
                <Plus className="h-4 w-4" />
                Set up your first campaign
              </Link>
            </Button>
          )}
        />
      ) : (
        <ul className="space-y-3">
          {campaigns.map((c) => (
            <li key={c.id}>
              <Link href={c.dashboard_path} className="group block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <Card className="card-hover-lift">
                  <div className="flex items-center gap-4 p-5">
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <h2 className="truncate font-display text-lg font-semibold text-foreground transition-colors group-hover:text-primary">{c.name}</h2>
                        <StatusBadge status={c.display_state || c.state} showActive />
                        {!c.approved && <Badge variant="warning">Awaiting approval</Badge>}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" aria-hidden="true" />
                          {c.dates_confirmed ? formatDateRange(c.start_at, c.end_at) : "Dates not set yet"}
                        </span>
                        <span>{pluralize(c.classroom_count, "classroom")}</span>
                        <span className="font-tabular">
                          {c.student_limit
                            ? `${formatNumber(c.student_count)} of ${formatNumber(c.student_limit)} students`
                            : pluralize(c.student_count, "student")}
                          {c.plan_name && ` · ${c.plan_name} plan`}
                        </span>
                        {c.money_goal_cents && <span className="font-tabular">Goal {formatMoney(c.money_goal_cents)}</span>}
                        {c.state === "grace" && c.grace_ends_at && (
                          <span className="text-warning-foreground">Donations close {formatDate(c.grace_ends_at)}</span>
                        )}
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden="true" />
                  </div>
                </Card>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </SchoolLayout>
  );
}
